import React, {useState} from 'react';
import {Eye, Calendar} from 'lucide-react';
import {StoreDeliveryTransaction, StoreDeliveryFilters} from '../../models/storeDeliveries.model';
import {AppText, AppLabelText} from '../../../../Presentation/components/ui/Typography';
import AppTable, {TableColumn} from '../../../../Presentation/components/ui/AppTable';
import AppTableHeaderCell from '../../../../Presentation/components/ui/AppTableHeaderCell';
import Button from '../../../../Presentation/components/ui/Button';

interface StoreDeliveryTimelineTableProps {
  transactions: StoreDeliveryTransaction[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  filters: StoreDeliveryFilters;
  onFiltersChange: (filters: StoreDeliveryFilters) => void;
  onPageChange: (page: number) => void;
  onViewTransaction: (transactionId: string) => void;
}

export const StoreDeliveryTimelineTable: React.FC<StoreDeliveryTimelineTableProps> = ({
  transactions,
  loading,
  error,
  pagination,
  filters,
  onFiltersChange,
  onPageChange,
  onViewTransaction
}) => {
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters = !!(filters.from_date || filters.to_date || filters.action_type || filters.source_type);
  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      from_date: undefined,
      to_date: undefined,
      action_type: undefined,
      source_type: undefined,
      page: 1
    });
  };

  const getActionTypeLabel = (type: string) => {
    switch (type) {
    case 'delivery': return 'تسليم';
    case 'return': return 'إرجاع';
    case 'adjustment': return 'تسوية';
    default: return type;
    }
  };

  const getActionTypeColor = (type: string) => {
    switch (type) {
    case 'delivery': return 'bg-green-100 text-green-800';
    case 'return': return 'bg-red-100 text-red-800';
    case 'adjustment': return 'bg-brand-100 text-brand-800';
    default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceTypeLabel = (type: string) => {
    switch (type) {
    case 'warehouse': return 'مستودع';
    case 'marketer': return 'مسوق';
    case 'provider': return 'مزود';
    case 'system': return 'نظام';
    default: return type;
    }
  };

  const emptyText = hasActiveFilters
    ? 'لا توجد نتائج تطابق الفلاتر. جرب تغيير نطاق التاريخ أو إزالة بعض الفلاتر.'
    : 'لا توجد تسليمات في هذه الفترة';

  const columns: TableColumn<StoreDeliveryTransaction>[] = [
    {
      key: 'date',
      title: <AppTableHeaderCell label="التاريخ والوقت" />,
      render: (transaction) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-900">{transaction.date}</div>
            <div className="text-xs text-gray-500">{transaction.time}</div>
          </div>
        </div>
      )
    },
    {
      key: 'source',
      title: <AppTableHeaderCell label="المصدر" />,
      render: (transaction) => (
        <div>
          <div className="text-sm text-gray-900">{getSourceTypeLabel(transaction.sourceType)}</div>
          {transaction.sourceName && (
            <div className="text-xs text-gray-500">{transaction.sourceName}</div>
          )}
        </div>
      )
    },
    {
      key: 'actionType',
      title: <AppTableHeaderCell label="نوع العملية" />,
      render: (transaction) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionTypeColor(transaction.actionType)}`}>
          {getActionTypeLabel(transaction.actionType)}
        </span>
      )
    },
    {
      key: 'itemsCount',
      title: <AppTableHeaderCell label="عدد القطع" />,
      render: (transaction) => <AppText>{transaction.itemsCount}</AppText>
    },
    {
      key: 'totalQuantity',
      title: <AppTableHeaderCell label="الكمية الإجمالية" />,
      render: (transaction) => (
        <AppText className="font-medium">{transaction.totalQuantity}</AppText>
      )
    }
  ];

  const actions = (transaction: StoreDeliveryTransaction) => [
    {
      label: 'عرض التفاصيل',
      icon: <Eye className="w-4 h-4" />,
      onClick: () => onViewTransaction(transaction.transactionId)
    }
  ];

  const handlePageSizeChange = (pageSize: number) => {
    onFiltersChange({...filters, limit: pageSize, page: 1});
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 shadow mt-5">
        <div className="flex items-center gap-3">
          <div className="text-red-600">⚠️</div>
          <div>
            <AppLabelText className="text-red-900">خطأ في التحميل</AppLabelText>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-xl shadow bg-secondary mt-5">
      {/* Header with Filters Toggle */}
      <div className="border-b border-gray-200 p-4 bg-white rounded-t-xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <AppLabelText className="text-gray-900 font-semibold">سجل التسليمات</AppLabelText>
          <Button
            variant="outline"
            size="small"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'إخفاء الفلاتر' : 'عرض الفلاتر'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="border-b border-gray-200 p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">من تاريخ</label>
              <input
                type="date"
                value={filters.from_date || ''}
                onChange={(e) => onFiltersChange({...filters, from_date: e.target.value || undefined})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">إلى تاريخ</label>
              <input
                type="date"
                value={filters.to_date || ''}
                onChange={(e) => onFiltersChange({...filters, to_date: e.target.value || undefined})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">نوع العملية</label>
              <select
                value={filters.action_type || ''}
                onChange={(e) => onFiltersChange({...filters, action_type: (e.target.value as StoreDeliveryFilters['action_type']) || undefined})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">الكل</option>
                <option value="delivery">تسليم</option>
                <option value="return">إرجاع</option>
                <option value="adjustment">تسوية</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">المصدر</label>
              <select
                value={filters.source_type || ''}
                onChange={(e) => onFiltersChange({...filters, source_type: (e.target.value as StoreDeliveryFilters['source_type']) || undefined})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">الكل</option>
                <option value="warehouse">مستودع</option>
                <option value="marketer">مسوق</option>
                <option value="provider">مزود</option>
                <option value="system">نظام</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Active filters chips */}
      {hasActiveFilters && (
        <div className="border-b border-gray-200 px-4 py-2 bg-gray-50/70 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">الفلاتر المطبقة:</span>
          {filters.from_date && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-100 text-brand-800 text-xs">
              من {filters.from_date}
              <button type="button" onClick={() => onFiltersChange({...filters, from_date: undefined, page: 1})} className="hover:text-brand-900" aria-label="إزالة">×</button>
            </span>
          )}
          {filters.to_date && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-100 text-brand-800 text-xs">
              إلى {filters.to_date}
              <button type="button" onClick={() => onFiltersChange({...filters, to_date: undefined, page: 1})} className="hover:text-brand-900" aria-label="إزالة">×</button>
            </span>
          )}
          {filters.action_type && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-100 text-brand-800 text-xs">
              نوع: {getActionTypeLabel(filters.action_type)}
              <button type="button" onClick={() => onFiltersChange({...filters, action_type: undefined, page: 1})} className="hover:text-brand-900" aria-label="إزالة">×</button>
            </span>
          )}
          {filters.source_type && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-brand-100 text-brand-800 text-xs">
              مصدر: {getSourceTypeLabel(filters.source_type)}
              <button type="button" onClick={() => onFiltersChange({...filters, source_type: undefined, page: 1})} className="hover:text-brand-900" aria-label="إزالة">×</button>
            </span>
          )}
          <button type="button" onClick={clearFilters} className="text-xs text-gray-600 hover:text-gray-900 underline">
            إزالة الكل
          </button>
        </div>
      )}

      {/* Table - unified with AppTable as in the rest of the app */}
      <div className="overflow-x-auto px-0">
        <AppTable<StoreDeliveryTransaction>
          columns={columns}
          data={transactions}
          rowKey={(row) => row.transactionId}
          emptyText={emptyText}
          page={pagination.page}
          pageSize={pagination.limit}
          totalCount={pagination.total}
          onPageChange={onPageChange}
          onPageSizeChange={handlePageSizeChange}
          showPageSizeSelector={true}
          showPaginationInfo={true}
          actions={actions}
          loading={loading}
          loadingRows={pagination.limit || 5}
          loadingMessage="جاري تحميل سجل التسليمات..."
          className="mt-0"
        />
      </div>
    </div>
  );
};
